import React, { useMemo, useState } from "react";
import { TableCell, TableRowProps } from "@mui/material";
import { TableConfig, ColumnConfig, TableRow } from "@components";

type GenericRow<T> = {
  settings: TableConfig<T>;
  row: T;
  isChildRow?: boolean;
  areSomeRowExpandable?: boolean;
  additionalProps?: Record<string, unknown>;
  columns?: ColumnConfig<T>[];
} & Omit<TableRowProps, "children">;

export const AutoTableRow = function <T extends { children?: T[] }>(
  props: GenericRow<T>
) {
  const {
    settings,
    row,
    isChildRow,
    additionalProps = {},
    columns,
    areSomeRowExpandable,
    sx,
  } = props;
  const [expanded, setExpanded] = useState(settings.expandedByDefault ?? false);

  const canExpand = settings.isRowExpandable?.(row);
  const canClick =
    !!settings.onRowClick && (settings.isRowClickable?.(row) ?? true);
  const useChildrenArray = !settings.childrenRenderer;

  const handleClick = () => {
    if (canExpand) {
      setExpanded(!expanded);
    } else if (canClick) {
      settings.onRowClick?.(row);
    }
  };

  const SubRows = useMemo(() => {
    if (!canExpand) return null;

    if (settings.childrenRenderer) {
      return settings.childrenRenderer(row);
    }

    return row.children?.map((child, idx) => (
      <AutoTableRow
        key={settings.getRowId?.(child, true) ?? idx}
        row={child}
        settings={settings}
        isChildRow
        columns={columns}
        additionalProps={additionalProps}
        areSomeRowExpandable={areSomeRowExpandable}
      />
    ));
  }, [
    canExpand,
    row,
    settings,
    columns,
    additionalProps,
    areSomeRowExpandable,
  ]);

  const columnDefs = columns ?? settings.columns;

  return (
    <TableRow
      data-testid="auto-table-row"
      onClick={canExpand || canClick ? handleClick : undefined}
      isClickable={canClick && !canExpand}
      isExpanded={expanded}
      expandableElement={SubRows}
      arrowPosition={settings.arrowPosition}
      isExpandedInTableCell={!useChildrenArray}
      reserveArrowSlot={Boolean(areSomeRowExpandable) && isChildRow}
      sx={sx}
    >
      {columnDefs.map((col) => {
        if (col.hidden) return null;

        const Renderer = col.renderer;
        const cellKey = `${settings.id}-${col.id}`;

        return (
          <TableCell key={cellKey} align={col.align}>
            <Renderer
              key={col.id}
              row={row}
              isChildRow={isChildRow}
              isExpanded={expanded}
              tableKey={settings.id}
              {...additionalProps}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
};
